// Credenciales de ingesta y usuario técnico (Admin · Integración de planta).
//
// La credencial completa (`jk_<prefijo>.<secreto>`) solo viaja en la
// respuesta de creación: se muestra UNA vez en un diálogo con botón de copiar
// y no se guarda en ningún estado persistente. Después solo se ve el prefijo.
// Crear exige el usuario técnico (el API responde 409 sin_usuario_tecnico).
// Revocar es POST …/:id/revocar, con confirmación.

import { useState } from 'react';
import { toast } from 'sonner';
import type { CredencialIngesta, CredencialIngestaCreada, UsuarioTecnicoIngesta } from 'schemas';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Icon } from '../../../components/ui/icon';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { TBody, TD, TH, THead, TR, Table } from '../../../components/ui/table';
import {
  useCrearCredencialIngesta,
  useCrearUsuarioTecnico,
  useCredencialesIngesta,
  useRevocarCredencialIngesta,
} from '../../../lib/api/ingesta';
import { tiempoRelativo } from '../../../lib/tiempoRelativo';
import { Seccion, mensajeError } from './comun';

export function UsuarioTecnicoSeccion({ usuarioTecnicoId }: { usuarioTecnicoId: string | null }) {
  const crear = useCrearUsuarioTecnico();
  const [resultado, setResultado] = useState<UsuarioTecnicoIngesta | null>(null);
  const configurado = usuarioTecnicoId !== null || resultado !== null;
  return (
    <Seccion
      titulo="Usuario técnico"
      descripcion="Las credenciales actúan en nombre de este usuario (rol líder, sin contraseña ni inicio de sesión). Toda la auditoría de la máquina queda a su nombre."
      accion={
        <Button
          variant={configurado ? 'secondary' : 'primary'}
          size="sm"
          loading={crear.isPending}
          onClick={() =>
            crear.mutate(undefined, {
              onSuccess: (u) => {
                setResultado(u);
                toast.success(
                  u.creado ? 'Usuario técnico creado' : 'El usuario técnico ya existía',
                );
              },
              onError: (e) => toast.error(mensajeError(e)),
            })
          }
        >
          {configurado ? 'Verificar' : 'Crear usuario técnico'}
        </Button>
      }
    >
      <div className="flex items-center gap-2 text-sm">
        {configurado ? (
          <Badge tone="ok" dot>
            Configurado
          </Badge>
        ) : (
          <Badge tone="warn" dot>
            Sin usuario técnico
          </Badge>
        )}
        {resultado ? (
          <span className="truncate text-[12px] text-fg-secondary">
            {resultado.nombre} · <span className="font-mono">{resultado.email}</span>
          </span>
        ) : null}
      </div>
    </Seccion>
  );
}

export function CredencialesSeccion({ hayUsuarioTecnico }: { hayUsuarioTecnico: boolean }) {
  const lista = useCredencialesIngesta();
  const crear = useCrearCredencialIngesta();
  const revocar = useRevocarCredencialIngesta();
  const [creando, setCreando] = useState(false);
  const [creada, setCreada] = useState<CredencialIngestaCreada | null>(null);
  const [aRevocar, setARevocar] = useState<CredencialIngesta | null>(null);
  const [errorCrear, setErrorCrear] = useState<string | null>(null);

  return (
    <Seccion
      titulo="Credenciales"
      descripcion="Una credencial por integrador o equipo de planta. Se envía en el encabezado Authorization: Bearer de cada petición a /api/ingesta."
      accion={
        <Button
          size="sm"
          onClick={() => {
            setErrorCrear(null);
            setCreando(true);
          }}
          disabled={!hayUsuarioTecnico}
          title={hayUsuarioTecnico ? undefined : 'Primero cree el usuario técnico'}
        >
          <Icon name="key" size={14} /> Nueva credencial
        </Button>
      }
    >
      {lista.isLoading ? (
        <p className="text-sm text-fg-tertiary">Cargando…</p>
      ) : lista.isError ? (
        <p role="alert" className="text-sm text-crit">
          {mensajeError(lista.error)}
        </p>
      ) : (
        <CredencialesTabla credenciales={lista.data ?? []} onRevocar={setARevocar} />
      )}

      {creando ? (
        <CrearCredencialDialog
          guardando={crear.isPending}
          serverError={errorCrear}
          onClose={() => setCreando(false)}
          onCrear={(nombre) =>
            crear.mutate(
              { nombre },
              {
                onSuccess: (c) => {
                  setCreando(false);
                  setCreada(c);
                  // El secreto vive solo en el estado del diálogo: fuera de la
                  // caché de mutaciones (gcTime 0 en el hook).
                  crear.reset();
                },
                onError: (e) => setErrorCrear(mensajeError(e)),
              },
            )
          }
        />
      ) : null}

      {creada ? (
        <CredencialCreadaDialog credencial={creada} onClose={() => setCreada(null)} />
      ) : null}

      {aRevocar ? (
        <Dialog open onClose={() => setARevocar(null)}>
          <DialogHeader>
            <DialogTitle>Revocar «{aRevocar.nombre}»</DialogTitle>
            <DialogDescription>
              La máquina que use esta credencial dejará de poder enviar datos de inmediato. No se
              puede deshacer: para reconectar hay que crear una credencial nueva.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setARevocar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={revocar.isPending}
              onClick={() =>
                revocar.mutate(aRevocar.id, {
                  onSuccess: () => {
                    toast.success('Credencial revocada');
                    setARevocar(null);
                  },
                  onError: (e) => toast.error(mensajeError(e)),
                })
              }
            >
              Revocar
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </Seccion>
  );
}

export function CredencialesTabla({
  credenciales,
  onRevocar,
}: {
  credenciales: CredencialIngesta[];
  onRevocar: (c: CredencialIngesta) => void;
}) {
  if (credenciales.length === 0) {
    return <p className="text-sm text-fg-tertiary">Aún no hay credenciales.</p>;
  }
  return (
    <Table>
      <THead>
        <TR>
          <TH>Nombre</TH>
          <TH>Credencial</TH>
          <TH>Estado</TH>
          <TH>Último uso</TH>
          <TH />
        </TR>
      </THead>
      <TBody>
        {credenciales.map((c) => (
          <TR key={c.id} className={c.activa ? '' : 'opacity-60'}>
            <TD className="text-fg">{c.nombre}</TD>
            <TD className="font-mono text-[12px] text-fg-secondary">jk_{c.prefijo}.••••••</TD>
            <TD>
              {c.activa ? <Badge tone="ok">Activa</Badge> : <Badge tone="neutral">Revocada</Badge>}
            </TD>
            <TD className="text-fg-secondary">
              {c.ultimo_uso_at ? tiempoRelativo(c.ultimo_uso_at) : 'nunca'}
            </TD>
            <TD align="right">
              {c.activa ? (
                <Button variant="ghost" size="sm" onClick={() => onRevocar(c)}>
                  Revocar
                </Button>
              ) : null}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function CrearCredencialDialog({
  guardando,
  serverError,
  onCrear,
  onClose,
}: {
  guardando: boolean;
  serverError: string | null;
  onCrear: (nombre: string) => void;
  onClose: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const limpio = nombre.trim();
  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Nueva credencial</DialogTitle>
        <DialogDescription>Un nombre que identifique quién la usará.</DialogDescription>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="credencial-nombre">Nombre</Label>
          <Input
            id="credencial-nombre"
            value={nombre}
            maxLength={80}
            placeholder="Ej. PLC envasado planta Rionegro"
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
        {serverError ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">
            {serverError}
          </p>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button onClick={() => onCrear(limpio)} disabled={!limpio} loading={guardando}>
          Crear credencial
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/**
 * Muestra la credencial completa recién creada. Es la ÚNICA vez que el
 * secreto está disponible: al cerrar se descarta.
 */
export function CredencialCreadaDialog({
  credencial,
  onClose,
}: {
  credencial: CredencialIngestaCreada;
  onClose: () => void;
}) {
  const [copiada, setCopiada] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(credencial.secreto);
      setCopiada(true);
    } catch {
      toast.error('No se pudo copiar. Seleccione el texto y cópielo manualmente.');
    }
  }

  return (
    <Dialog open onClose={onClose} forceConfirm className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Credencial creada</DialogTitle>
        <DialogDescription>«{credencial.nombre}»</DialogDescription>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div
          role="alert"
          className="flex items-start gap-2 rounded border border-warn/40 bg-warn-soft px-3 py-2 text-xs text-warn"
        >
          <Icon name="alert" size={14} className="mt-0.5" />
          <span>
            Copie la credencial ahora y guárdela en un lugar seguro.{' '}
            <strong>No se volverá a mostrar</strong>: si se pierde, hay que revocarla y crear otra.
          </span>
        </div>
        <div className="flex items-stretch gap-2">
          <code
            data-testid="credencial-secreto"
            className="min-w-0 flex-1 select-all break-all rounded border border-border bg-surface-input px-3 py-2 font-mono text-[12px] text-fg"
          >
            {credencial.secreto}
          </code>
          <Button
            variant="secondary"
            onClick={() => void copiar()}
            aria-label="Copiar credencial"
            className="h-auto"
          >
            <Icon name={copiada ? 'check' : 'copy'} size={15} />
            {copiada ? 'Copiada' : 'Copiar'}
          </Button>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button onClick={onClose}>Ya la guardé</Button>
      </DialogFooter>
    </Dialog>
  );
}
